/**
 * 🦜 Parrot PWA Color System — iOS-EXACT
 *
 * Ported verbatim from client-native/src/ui/colors.ts (the source of truth).
 * Use these JS constants when you need a color in TS/JSX. They are the SAME
 * hex values the iOS app renders, so the PWA matches pixel-for-pixel.
 *
 * NOTE: The correct iOS values are green #34A853 and darkBlue #003d8e. The
 * legacy Chakra `theme.ts` once drifted from these but now derives its brand
 * palette from this file. Prefer these constants (or the CSS vars in
 * variables.css) over reaching into theme.ts directly.
 *
 * Usage:
 *   import { colors } from "@/ui/colors";
 *   style={{ backgroundColor: colors.orange, color: colors.text.primary }}
 */

// Main Parrot Colors — exact mirror of iOS `Colors`
export const Colors = {
  // Brand
  Orange: "#f06b00",
  Blue: "#0c92f1",
  DarkBlue: "#003d8e",
  SkyLightest: "#e8f4fc",
  Green: "#34A853",
  Yellow: "#fbba00",
  Red: "#c72d25",
  // iOS activity (lesson-button) palette — distinct from the brand Green/Red
  // (iOS colors.ts: ActivityCorrect / ActivityIncorrect).
  ActivityCorrect: "#2FBF71",
  ActivityIncorrect: "#E5484D",

  // Light variants
  LightYellow: "#FFEBA1",
  LightBlue: "#b6d9f2",
  LightBlueTranslucent: "#F2F8FF",
  LightGreenTranslucent: "#E6F9E6",

  // Neutrals
  Background: "#fafafa",
  Grey: "#424242",
  GreyLight: "#e1e1e1",
  GreyOther: "#6b7280",
  GreyLightest: "#f5f5f5",
  GreyProfile: "#A0AEC0",
  GreyChatBubble: "#F2F4F5",

  // Semantic
  Primary: "#111827",
  Secondary: "#424242",
  Accent: "#0c92f1",
  Muted: "#6b7280",
  Error: "#c72d25",
  Success: "#34A853",
  Warning: "#fbba00",

  // Common
  Black: "#000000",
  White: "#ffffff",
  Transparent: "rgba(0, 0, 0, 0)",

  // Translucent overlays
  WhiteSubtle: "rgba(255,255,255,0.10)",
  WhiteMedium: "rgba(255,255,255,0.20)",
  WhiteBold: "rgba(255,255,255,0.35)",
  WhiteOverlay: "rgba(255,255,255,0.55)",
  BlackOverlay: "rgba(0,0,0,0.7)",
} as const;

export const colors = {
  orange: Colors.Orange,
  blue: Colors.Blue,
  darkBlue: Colors.DarkBlue,
  skyLightest: Colors.SkyLightest,
  green: Colors.Green,
  yellow: Colors.Yellow,
  red: Colors.Red,
  activityCorrect: Colors.ActivityCorrect,
  activityIncorrect: Colors.ActivityIncorrect,
  lightYellow: Colors.LightYellow,
  lightBlue: Colors.LightBlue,
  lightBlueTranslucent: Colors.LightBlueTranslucent,
  lightGreen: Colors.LightGreenTranslucent,
  greyProfile: Colors.GreyProfile,
  greyChatBubble: Colors.GreyChatBubble,

  background: Colors.Background,
  grey: Colors.Grey,
  greyLight: Colors.GreyLight,
  greyOther: Colors.GreyOther,
  greyLightest: Colors.GreyLightest,

  primary: Colors.Primary,
  secondary: Colors.Secondary,
  accent: Colors.Accent,
  muted: Colors.Muted,
  error: Colors.Error,
  success: Colors.Success,
  warning: Colors.Warning,

  black: Colors.Black,
  white: Colors.White,
  transparent: Colors.Transparent,

  whiteSubtle: Colors.WhiteSubtle,
  whiteMedium: Colors.WhiteMedium,
  whiteBold: Colors.WhiteBold,
  whiteOverlay: Colors.WhiteOverlay,
  blackOverlay: Colors.BlackOverlay,

  text: {
    primary: Colors.Primary,
    secondary: Colors.Secondary,
    accent: Colors.Accent,
    muted: Colors.Muted,
    error: Colors.Error,
    success: Colors.Success,
    white: Colors.White,
  },

  brand: {
    primary: Colors.Orange,
    secondary: Colors.Blue,
    tertiary: Colors.Green,
  },

  neutral: {
    background: Colors.Background,
    surface: Colors.White,
    border: Colors.GreyLight,
    text: Colors.Primary,
    textMuted: Colors.Muted,
  },

  semantic: {
    success: Colors.Success,
    warning: Colors.Warning,
    error: Colors.Error,
    info: Colors.Accent,
  },
} as const;

export default colors;
